import { Skeleton, Space } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik, setNestedObjectValues } from 'formik';
import { MailOutlined } from '@ant-design/icons';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import Notification from 'components/ui/base/notification/Notification';
import Document from './Document';
import CommercialInvoiceDocument from './CommercialInvoiceDocument';
import { StyledList } from './styles';
import { useInvoiceContext } from '../../InvoiceContextProvider';
import { useRequestPromise } from 'hooks/useRequest';
import { getInvoiceFiles } from 'store/invoices/client';
import { InvoiceFileState, InvoiceFileTypes } from 'store/invoices/types';
import FilePreviewModal from 'components/new-ui/composed/modal/file-preview-modal/FilePreviewModal';
import useFilePreview from 'components/new-ui/composed/modal/file-preview-modal/useFilePreview';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import { validationSchema } from './validationSchema';
import { isValidInvoiceFile } from './utils';
import { getErrorMessage } from 'utils/getErrorMessage';
import { Events } from 'utils/events.enum';

type SendToCustomerFormValues = {
    email: string | null;
} & Partial<Record<InvoiceFileTypes, string | null>>;

type Props = {
    open: boolean;
    onClose: () => void;
};

const SendToCustomerModal = ({ open, onClose }: Props) => {
    const { invoice, sendInvoiceToCustomer } = useInvoiceContext();
    const { previewState, openPreviewWithBlob, setGenerating, closePreview } = useFilePreview();
    const { data, isLoading, error } = useRequestPromise(
        () => (invoice?.id && open ? getInvoiceFiles(invoice.id) : null),
        {},
        [invoice?.id, open]
    );

    const [loadingFileType, setLoadingFileType] = useState<InvoiceFileTypes | null>(null);

    // Required so the BE can deduplicate requests
    const idempotencyKeyRef = useRef<string | null>(null);

    useEffect(() => {
        idempotencyKeyRef.current = open ? uuidv4() : null;
    }, [open]);

    const initialValues = useMemo(
        () => ({
            email: data?.defaultRecipient ?? null,
            ...Object.fromEntries(
                (data?.files ?? []).map((file) => [
                    file.type,
                    file.state === InvoiceFileState.PRESENT ? file.fileReference : null,
                ])
            ),
        }),
        [data]
    );

    const formik = useFormik<SendToCustomerFormValues>({
        initialValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit: () => undefined,
    });

    const handlePreview = useCallback(
        async (url: string, fileType: InvoiceFileTypes) => {
            try {
                setLoadingFileType(fileType);
                setGenerating(true);
                const response = await fetch(url);
                if (!response.ok) throw new Error('Could not fetch file');
                const blob = await response.blob();
                openPreviewWithBlob(blob);
            } catch {
                setGenerating(false);
                Notification({
                    messageTitle: 'Error',
                    description: 'Could not load the file preview. Please try again.',
                    type: 'error',
                    placement: 'top',
                });
            } finally {
                setLoadingFileType(null);
            }
        },
        [openPreviewWithBlob, setGenerating]
    );

    const handleSubmit = async () => {
        if (formik.isSubmitting) return;

        const errors = await formik.validateForm();

        if (Object.keys(errors).length > 0) {
            formik.setTouched(setNestedObjectValues(errors, true));

            const errorMessages = Object.values(errors);
            const description = `Please fix the following errors: \n${errorMessages.join('\n')}`;

            Notification({
                messageTitle: 'Operation Failed',
                description,
                type: 'error',
            });
            return;
        }

        if (!invoice?.id) {
            Notification({
                type: 'error',
                messageTitle: 'Missing invoice data',
                description: DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
            return;
        }

        if (!formik.values.email || !formik.values.CUSTOMS_INVOICE || !formik.values.SAD) {
            return;
        }

        if (!idempotencyKeyRef.current) {
            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
            return;
        }

        const files = [
            {
                type: InvoiceFileTypes.CUSTOMS_INVOICE,
                fileReference: formik.values.CUSTOMS_INVOICE,
            },

            {
                type: InvoiceFileTypes.SAD,
                fileReference: formik.values.SAD,
            },
        ];

        if (formik.values.COMMERCIAL_INVOICE) {
            files.push({
                type: InvoiceFileTypes.COMMERCIAL_INVOICE,
                fileReference: formik.values.COMMERCIAL_INVOICE,
            });
        }

        formik.setSubmitting(true);

        try {
            await sendInvoiceToCustomer(invoice?.id, {
                recipient: formik.values.email,
                idempotencyKey: idempotencyKeyRef.current,
                files,
            });

            Notification({
                type: 'success',
                messageTitle: 'Operation Successful',
                description: 'Successfully sent invoice files to customer.',
            });

            window.EventBus.publish(Events.INVOICE_HISTORY_UPDATED);

            onClose();
        } catch (err) {
            const errorMessage = getErrorMessage(err) || DEFAULT_INTERNAL_ERROR_MESSAGE;
            Notification({
                type: 'error',
                messageTitle: 'Operation Failed.',
                description: errorMessage,
            });
        } finally {
            formik.setSubmitting(false);
        }
    };

    useEffect(() => {
        if (!error) return;

        Notification({
            messageTitle: 'Could not get invoice files',
            description: DEFAULT_INTERNAL_ERROR_MESSAGE,
            type: 'error',
        });
    }, [error]);

    return (
        <>
            <ConfirmationModal
                title="Send to Customer"
                confirmButtonText="Send"
                icon={<MailOutlined />}
                open={open}
                onClose={onClose}
                onCancel={onClose}
                onConfirm={handleSubmit}
                confirmationButtonLoading={formik.isSubmitting}
                content={
                    <>
                        <div style={{ display: 'flex', alignItems: 'start', gap: 90, width: '100%' }}>
                            <Space size={4} style={{ paddingTop: 6 }}>
                                <LabelRequiredSymbol />
                                <label htmlFor="email">To</label>
                                <span>:</span>
                            </Space>

                            <div style={{ width: '100%' }}>
                                <FormInput
                                    placeholder="Enter email address"
                                    fieldProps={formik.getFieldProps('email')}
                                    fieldHelper={formik.getFieldHelpers('email')}
                                    fieldMeta={formik.getFieldMeta('email')}
                                />
                            </div>
                        </div>
                        {isLoading ? (
                            <Skeleton active paragraph={{ rows: 3 }} />
                        ) : (
                            <StyledList>
                                {data?.files.map((file) => {
                                    const previewLoading = previewState.generating && loadingFileType === file.type;

                                    if (file.type === InvoiceFileTypes.COMMERCIAL_INVOICE) {
                                        return (
                                            <CommercialInvoiceDocument
                                                key={file.type}
                                                fileId={file.fileId}
                                                fileName={file.fileName}
                                                fileReference={file.fileReference}
                                                state={file.state}
                                                onPreview={
                                                    file.downloadUrl
                                                        ? () => handlePreview(file.downloadUrl, file.type)
                                                        : undefined
                                                }
                                                onPreviewWithUrl={(url) => handlePreview(url, file.type)}
                                                previewLoading={previewLoading}
                                                onUpload={(fileReference) =>
                                                    formik.setFieldValue(
                                                        InvoiceFileTypes.COMMERCIAL_INVOICE,
                                                        fileReference
                                                    )
                                                }
                                            />
                                        );
                                    }
                                    return (
                                        <Document
                                            key={file.type}
                                            isHighlighted={!isValidInvoiceFile(file)}
                                            fileName={file.fileName}
                                            documentType={file.type}
                                            onPreview={
                                                file.downloadUrl
                                                    ? () => handlePreview(file.downloadUrl, file.type)
                                                    : undefined
                                            }
                                            previewLoading={previewLoading}
                                        />
                                    );
                                })}
                            </StyledList>
                        )}
                    </>
                }
            />

            <FilePreviewModal
                open={previewState.open}
                url={previewState.url ?? ''}
                contentType={previewState.contentType}
                onClose={closePreview}
            />
        </>
    );
};

export default SendToCustomerModal;
